import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreateArtifactContext } from "../../../context/CreateArtifactContext";
import { useNavigationBar } from "../../../context/NavigationContext";
import { SKPlaceArtifact, ContentState } from "../../../models/SKArtifact";
import "react-datepicker/dist/react-datepicker.css";
import { AddPlaceField } from "./container/components/AddPlaceField";
// import { AddTextField } from "./container/components/AddTextField";
// import { AddTextAreaField } from "./container/components/AddTextAreaField";
import * as Yup from "yup";
import { isEditable } from "@testing-library/user-event/dist/utils";
import { AddTextField } from "../../../components/AddTextField";
import { AddTextAreaField } from "../../../components/AddTextAreaField";
import { capitalizeFirstLetter } from "../../../utils/stringFunctions";
import { PlaceURLLink } from "../../../components/PlaceURLLink";

export default function AddPlace() {
  const { contentData, artifactId, updateContentData, loading, saveData, isEditable } = useContext(CreateArtifactContext);
  const { setNavProps } = useNavigationBar();
  const navigate = useNavigate();
  const [editable, setEditable] = useState<boolean>(true);
  const [isValidForm, setIsValidForm] = useState(false);
  const [placeData, setPlaceData] = useState<SKPlaceArtifact | undefined>(undefined);
  const [error, setError] = useState<string>("");
  const [address, setAddress] = useState("");
  const [placeName, setPlaceName] = useState("");

  const schema = Yup.object().shape({
    placeDecription: Yup.string().notRequired(),
    location: Yup.object({
      latitude: Yup.number().required("Latitude is required"),
      longitude: Yup.number().required("Longitude is required"),
      title: Yup.string().required("Title is required"),
      address: Yup.string().required("Address is required"),
      url: Yup.string().notRequired(),
    }),
  });

  useEffect(() => {
    const validate = async () => {
      try {
        await schema.validate(contentData, { abortEarly: false });
        setIsValidForm(true);
      } catch (err) {
        setIsValidForm(false);
      }
    };
    validate();
  }, [contentData, schema]);

  useEffect(() => {
    if (contentData.status === ContentState.DRAFT) {
      setEditable(true);
    } else {
      setEditable(false);
    }
  }, [contentData]);

  useEffect(() => {
    setNavProps(prev => ({
      ...prev,
      backArrow: false,
      buttonTitle: "Done",
      title: "Add Place",
      enableLink: isEditable ? isValidForm : false,
      getActionItems: undefined,
      onNext: isEditable ? submit : undefined,
      onClose: () => navigate(-1),
      onEdit: undefined,
    }));
  }, [setNavProps, placeData, isEditable]);

  useEffect(() => {
    setPlaceData(contentData as SKPlaceArtifact);
  }, [contentData]);

  const submit = async () => {
    await saveData();
    window.history.back();
  };

  const processInput = (inputValue: string) => {
    if (inputValue) {
      const formattedResult = inputValue
        .split(",")
        .map(item => item.trim())
        .filter(item => item.length > 0)
        .join(", \n");
      var locationName = inputValue.split(",")[0].trim();
      setPlaceName(locationName);
      setAddress(formattedResult);
      updateContentData({
        placeAddress: formattedResult,
        placeName: locationName,
      });
    }
  };

  return (
    <>
      <div className="flex flex-col gap-[30px] mt-[12px]">
        <AddPlaceField
          name={"addplace"}
          title={"Add Place"}
          note={"Search for the place or location."}
          placeholder={"Search Place"}
          index={1}
          isVisible={true}
          value={(contentData as SKPlaceArtifact).location?.title}
          onChange={(formattedAddress, loc, url, title) => {
            const location = url
              ? {
                  latitude: loc.lat,
                  longitude: loc.lng,
                  title: capitalizeFirstLetter(title),
                  address: capitalizeFirstLetter(formattedAddress),
                  url,
                }
              : {
                  latitude: loc.lat,
                  longitude: loc.lng,
                  title: capitalizeFirstLetter(title),
                  address: capitalizeFirstLetter(formattedAddress),
                };
            updateContentData({
              location,
            });
          }}
          disabled={!isEditable}
        />
        <AddTextField
          name={"placename"}
          title={"Add Place Name"}
          note={"System will try to auto add the place name from the selected search result. You can manually add or edit the place name."}
          placeholder={"Place Name"}
          index={2}
          charAllowed={150}
          value={(contentData as SKPlaceArtifact).location?.title ? (contentData as SKPlaceArtifact).location.title : placeName}
          onChange={async e => {
            const location = { ...(contentData as SKPlaceArtifact).location, title: e.target.value };
            updateContentData({ location });
          }}
          disabled={!isEditable}
        />
        <AddTextAreaField
          name={"placeaddress"}
          title={"Add Place Address"}
          note={"System will try to auto add the place address from the selected search result. You can manually add or edit the place address."}
          placeholder={"Place Address"}
          index={3}
          height={110}
          charAllowed={300}
          value={(contentData as SKPlaceArtifact).location?.address ? (contentData as SKPlaceArtifact).location?.address : address}
          onChange={async e => {
            const location = { ...(contentData as SKPlaceArtifact).location, address: e.target.value };
            updateContentData({ location });
          }}
          disabled={!isEditable}
        />

        <PlaceURLLink
          name={"placeUrl"}
          title={"Place Website"}
          value={(contentData as SKPlaceArtifact).location?.url ?? ""}
          index={4}
          onRemove={() => {
            const location = { ...(contentData as SKPlaceArtifact).location, url: "" };
            updateContentData({ location });
          }}
        />

        <AddTextAreaField
          name={"placedescription"}
          title={"Add Place Description"}
          note={"Details, URL links, or explainer of the place."}
          placeholder={"Place Description"}
          index={5}
          height={110}
          charAllowed={300}
          value={(contentData as SKPlaceArtifact).placeDescription}
          onChange={async e => {
            updateContentData({
              placeDescription: e.target.value,
            });
          }}
          disabled={!isEditable}
        />
      </div>
    </>
  );
}
